import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { Select } from '@layout/interfaces/select';
import {
	alinharDado,
	baixarEAbrirPDF,
	capitalizeFirstLetter,
	criarImagemBase64FromChart,
	filterSelectOptions,
	isNuloOuUndefined,
	numberToBrNumberTables,
	verificaSePDFEAdicionaNaTabela,
} from '@utils';
import * as pdfseira from '@utils/pdf-seira';
import { formatISO } from 'date-fns';
import { Chart } from 'highcharts';
import { ToastrService } from 'ngx-toastr';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { Subject, takeUntil } from 'rxjs';
import {
	AgrupamentoIAPM,
	FormularioRelatorio,
	IapmDTO,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
	PeriodosLabel,
} from '../../../interfaces';
import {
	GraficosPrecipitacaoService,
	RelatoriosService,
} from '../../../services';
import { gerarFilenameGrafico, obterLabelPeriodo } from '../../../utils';
import { DateTimeUtils } from '@utils/datetime-util';
import moment from 'moment';
import { DocumentExporter } from '@utils/document-exporter';
import * as ExcelTable from 'mr-excel';
import { NumberUtils } from '@utils/number-utils';

export const PERIODO_MINIMO_IAPM_EM_ANOS = 2;
export const PERIODO_MAXIMO_IAPM_EM_ANOS = 10;

export const PERIODO_MINIMO_IAPM_EM_MESES = 2;
export const PERIODO_MAXIMO_IAPM_EM_MESES = 12;

export const PERIODO_MINIMO_IAPM_EM_DIAS = 182;
export const PERIODO_MAXIMO_IAPM_EM_DIAS = 366;
@Component({
	selector: 'seira-pluviometria-grafico-iapm',
	templateUrl: './pluviometria-grafico-iapm.component.html',
	styleUrls: ['./pluviometria-grafico-iapm.component.scss'],
})
export class PluviometriaGraficoIapmComponent
	implements OnInit, OnDestroy, InstanciaRelatorio
{
	inputs = inject(INPUTS_RELATORIOS);
	form!: FormGroup<any>;
	isLoading = false;
	chart!: Chart;
	dados?: IapmDTO[];

	descricaoRelatorio =
		'Define-se como IAPM, Índice de Anomalia de Precipitação Modificado, a variação da pluviometria em comparação com valores médios históricos a fim de caracterizar as diferentes intensidades de eventos secos e chuvosos no tempo e no espaço.';

	referenciaEstudo =
		'Os métodos utilizados para a análise da precipitação e cálculo do Índice de Anomalia de Chuva (IAC) foram baseados no estudo intitulado: "Índice de Anomalia de Chuva (IAC) como Indicador para Análise da Variabilidade Climática na Bacia Hidrográfica do Rio Pajeú-PE", de autoria de J. M. O. Assis, W. M. Souza, M. C. M. Sobral, G. L. Melo e R. A. Irmão. Este trabalho foi apresentado no XX Simpósio Brasileiro de Recursos Hídricos em 2013 e fornece uma aplicação prática do IAC para a análise da variabilidade climática. O estudo está disponível no repositório da Associação Brasileira de Recursos Hídricos (ABRHidro).';

	botoesDeExportacao: GroupButton[] = [
		{
			label: '.png',
			size: 'small',
			icon: 'ph-image',
			onClick: () => {
				return this.exportarPNG();
			},
		},
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () => {
				return this.exportarPDF();
			},
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];
	fileName = '';
	chartTitle = '';
	columnColor?: string;
	periodoTitulo = '';
	agrupamento: Select<string>[];
	private _destroyed = new Subject();
	relatorioService = inject(RelatoriosService);
	tituloAgrupamento = '';

	constructor(
		private readonly service: GraficosPrecipitacaoService,
		private toastr: ToastrService
	) {
		this.agrupamento = filterSelectOptions(
			AgrupamentoIAPM,
			AgrupamentoIAPM.MICRORREGIAO,
			AgrupamentoIAPM.MESORREGIAO,
			AgrupamentoIAPM.BACIA_SUBBACIA,
			AgrupamentoIAPM.REGIAO_PLUVIOMETRICA
		);
	}

	ngOnInit(): void {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValue(this.agrupamento[0].value);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValue('mensal', { emitEvent: false });

		this.setValidatorsByAgrupamento(
			this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value
		);
		this.setDatasByPeriodoBusca(
			this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.value
		);
		this.setValidators();
	}

	ngOnDestroy(): void {
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.ESTACAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MUNICIPIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MICRORREGIAO)?.clearValidators();
		this._destroyed.next(undefined);
	}

	onChartCreated(chart: Chart) {
		this.chart = chart;
	}

	gerarRelatorio() {
		if (this.inputs.form?.invalid) {
			return;
		}

		const agrupamento = this.inputs.form?.get('agrupamento')?.value;
		const periodoBusca = this.inputs.form?.get('periodoBusca')?.value;
		const dataInicio = this.inputs.form?.get('dataInicio')?.value;
		const dataFim = this.inputs.form?.get('dataFim')?.value;

		let id = 0;

		switch (agrupamento) {
			case 'MICRORREGIAO':
				id = this.getDataFromForm(FormularioRelatorio.MICRORREGIAO)?.value;
				break;
			case 'MESORREGIAO':
				id = this.getDataFromForm(FormularioRelatorio.MESORREGIAO)?.value;
				break;
			case 'REGIAO_PLUVIOMETRICA':
				id = this.getDataFromForm(
					FormularioRelatorio.REGIAO_PLUVIOMETRICA
				)?.value;
				break;
			case 'BACIA_SUBBACIA':
				id = this.getDataFromForm(FormularioRelatorio.BACIA)?.value;
				break;
		}

		if (id == 0 || !agrupamento || !periodoBusca || !dataInicio || !dataFim) {
			return;
		}

		const periodoFormatado = DateTimeUtils.formatarDataPeriodo(
			dataInicio,
			dataFim,
			periodoBusca
		);
		const labelPeriodo = obterLabelPeriodo(periodoBusca);
		this.periodoTitulo = `${labelPeriodo} (${periodoFormatado})`;

		this.handleFetchIAPM(
			id,
			agrupamento,
			this.handleFormatPeriodoBusca(periodoBusca),
			formatISO(new Date(dataInicio)),
			formatISO(new Date(dataFim))
		);
	}

	getDadosTabelaParaExportacao(dados: IapmDTO[], isPdf: boolean) {
		const tableData: any[][] = [];
		const agrupamento = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;
		const periodoBusca = this.inputs.form?.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value;

		const colunas = [
			{
				text: AgrupamentoIAPM[agrupamento as keyof typeof AgrupamentoIAPM],
				fillColor: '#DCDCDC',
				alignment: 'center',
			},
			{ text: 'IAPM (mm)', fillColor: '#DCDCDC', alignment: 'center' },
			{ text: 'Classificação', fillColor: '#DCDCDC', alignment: 'center' },
			{
				text: 'Data',
				fillColor: '#DCDCDC',
				alignment: 'center',
			},
		];

		verificaSePDFEAdicionaNaTabela(tableData, colunas, isPdf);

		dados.forEach((item: IapmDTO) => {
			const rowData = [
				agrupamento === 'MUNICIPIO_POSTO'
					? item.nome
					: alinharDado(item.nome, isPdf),
				alinharDado(numberToBrNumberTables(item.iapm, 1), isPdf),
				alinharDado(item.classificacao, isPdf),
				periodoBusca === 'mensal'
					? alinharDado(item.periodo, isPdf)
					: alinharDado(
							DateTimeUtils.formatarData(item.periodo, 'yyyy'),
							isPdf
						),
			];
			tableData.push(rowData);
		});

		return tableData;
	}

	retornarIAPMParaXLSX() {
		const iapms: any[] = [];

		this.dados?.forEach(iapm => {
			iapms.push({
				nome: iapm.nome,
				iapm: isNuloOuUndefined(iapm.iapm)
					? '-'
					: NumberUtils.getIgnorandoCasasDecimais(iapm.iapm),
				classificacao: iapm.classificacao,
				periodo: !isNaN(Number(iapm.periodo))
					? Number(iapm.periodo)
					: iapm.periodo,
			});
		});

		return iapms;
	}
	private carregarImagem(src: string): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = err => reject(err);
			img.src = src;
		});
	}

	private async getLogoBase64(caminho: string): Promise<string | null> {
		try {
			const logo = await this.carregarImagem(caminho);
			const canvas = document.createElement('canvas');
			canvas.width = logo.width;
			canvas.height = logo.height;
			const ctx = canvas.getContext('2d')!;
			ctx.drawImage(logo, 0, 0);
			return canvas.toDataURL('image/png');
		} catch (e) {
			console.error(`[IAPM] Erro ao carregar logo ${caminho}:`, e);
			return null;
		}
	}
	async exportarPDF() {
		const chartsConverted: TDocumentDefinitions['content'] = [];

		if (!this.chart || !this.dados) {
			this.toastr.error(
				'Não é possível gerar um pdf pois nenhum gráfico foi gerado.'
			);
			return;
		}

		chartsConverted.push({
			image: await criarImagemBase64FromChart(this.chart),
			fontSize: 10,
			alignment: 'center',
			height: 400,
			width: 700,
		});

		const documentDefinition: any =
			await pdfseira.documentDefinitions('landscape');

		documentDefinition.pageOrientation = 'landscape';

		const logoAesaBase64 = await this.getLogoBase64(
			'assets/images/logos/logoAesa.svg'
		);
		const logoGovernoBase64 = await this.getLogoBase64(
			'assets/images/logos/governo_pb.png'
		);

		if (logoAesaBase64 || logoGovernoBase64) {
			documentDefinition.images = documentDefinition.images || {};

			if (logoAesaBase64) {
				documentDefinition.images['aesaLogo'] = logoAesaBase64;
			}

			if (logoGovernoBase64) {
				documentDefinition.images['governoLogo'] = logoGovernoBase64;
			}

			documentDefinition.footer = (currentPage: number, pageCount: number) => {
				return {
					margin: [40, 0, 40, 20],
					columns: [
						{
							image: 'aesaLogo',
							width: 70,
							alignment: 'left',
						},
						{
							text: `Página ${currentPage} de ${pageCount}`,
							alignment: 'center',
							fontSize: 8,
							width: '*',
						},
						{
							image: 'governoLogo',
							width: 70,
							alignment: 'right',
						},
					],
				};
			};
		}

		const tituloPdf = `Índice de anomalia de precipitação modificado (IAPM) - ${this.periodoTitulo}`;

		const content = [
			{
				text: tituloPdf,
				alignment: 'center',
				margin: [0, 15, 5, 15],
			},
			...chartsConverted,
		];

		documentDefinition.content.push(content);
		documentDefinition.content.push({
			table: {
				widths: ['30%', '20%', '30%', '20%'],
				body: this.getDadosTabelaParaExportacao(this.dados, true),
				layout: {
					noWrap: false,
					fontSize: 5,
				},
			},

			marginLeft: 30,
			marginTop: 10,
		});

		baixarEAbrirPDF(tituloPdf, documentDefinition);
	}

	exportarCSV() {
		const tableData = this.getDadosTabelaParaExportacao(
			this.dados as IapmDTO[],
			false
		);

		DocumentExporter.gerarCSV(
			tableData,
			`Índice de anomalia de precipitação modificado (IAPM) - ${this.periodoTitulo}`
		);
	}

	exportarTXT() {
		const tableData = this.getDadosTabelaParaExportacao(
			this.dados as IapmDTO[],
			false
		);
		let txtData = '';

		if (tableData.length > 0) {
			const colunas = tableData[0];

			tableData.slice(1).forEach(dadoRelatorio => {
				colunas.forEach((coluna: string, index: number) => {
					const valor = dadoRelatorio[index];
					txtData += `${coluna}: ${valor ?? '-'}\n`;
				});

				txtData += '\n';
			});
		}

		DocumentExporter.gerarTXT(
			txtData,
			`Índice de anomalia de precipitação modificado (IAPM) - ${this.periodoTitulo}`
		);
	}

	exportarXLSX() {
		const agrupamento = this.inputs.form.get('agrupamento')?.value;
		const nomeArquivo = `Índice de anomalia de precipitação modificado (IAPM) - ${this.periodoTitulo}`;
		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};
		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const headers = [
			{
				label: 'nome',
				text: AgrupamentoIAPM[agrupamento as keyof typeof AgrupamentoIAPM],
				size: 24,
			},
			{
				label: 'iapm',
				text: 'IAPM (mm)',
				size: 24,
			},
			{
				label: 'classificacao',
				text: 'Classificação',
				size: 24,
			},
			{
				label: 'periodo',
				text: 'Data',
				size: 24,
			},
		];
		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleCenter,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number,
						fromHeader: boolean,
						styleKeys: string[]
					) {
						if (data && data.label) {
							return 'headerStyleCenter';
						} else {
							if (colIndex === 0 && agrupamento === 'MUNICIPIO_POSTO') {
								return 'rowStyle';
							} else {
								return 'rowStyleCenter';
							}
						}
					},
					headers: headers,
					data: this.retornarIAPMParaXLSX(),
					columns: [
						{ key: 'iapm' },
						{ key: 'classificacao' },
						{ key: 'periodo' },
					],
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: nomeArquivo,
						styleId: 'title',
					},
				},
			],
			fileName: nomeArquivo,
		};

		ExcelTable.generateExcel(dataExcel);
	}

	setDatasByPeriodoBusca(periodoBuscaValue: PeriodosLabel | null) {
		const dataInicio = this.getDataFromForm(FormularioRelatorio.DATA_INICIO);
		const dataFim = this.getDataFromForm(FormularioRelatorio.DATA_FIM);
		if (dataInicio && dataFim) {
			const hoje = new Date();

			switch (periodoBuscaValue) {
				case 'anual':
				case 'anual_parcial':
					dataFim.setValue(moment(hoje).subtract(1, 'year').endOf('year'));
					dataInicio.setValue(moment(hoje).subtract(4, 'year').startOf('year'));
					break;
				case 'mensal':
					dataFim.setValue(moment(hoje).subtract(1, 'month').endOf('month'));
					dataInicio.setValue(
						moment(hoje)
							.subtract(PERIODO_MAXIMO_IAPM_EM_MESES, 'month')
							.startOf('month')
					);
					break;
			}
		}
	}

	setValidatorsByAgrupamento(agrupamentoValue: string) {
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.BACIA);
		this.handleRemoveValidatorsFromFormControl(
			FormularioRelatorio.MICRORREGIAO
		);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.MESORREGIAO);
		this.handleRemoveValidatorsFromFormControl(
			FormularioRelatorio.REGIAO_PLUVIOMETRICA
		);

		switch (agrupamentoValue) {
			case 'BACIA_SUBBACIA':
				this.inputs.form
					?.get(FormularioRelatorio.BACIA)
					?.setValidators(Validators.required);
				break;
			case 'MICRORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MICRORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'MESORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MESORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'REGIAO_PLUVIOMETRICA':
				this.inputs.form
					?.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
					?.setValidators(Validators.required);
				break;
		}
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: value => {
					this.setValidatorsByAgrupamento(value);
				},
			});

		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: (periodo: PeriodosLabel | null) => {
					this.setDatasByPeriodoBusca(periodo);
				},
			});

		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
	}

	private handleFetchIAPM(
		id: number,
		agrupamento: string,
		periodo: string,
		dataInicial: string,
		dataFinal: string
	) {
		this.inputs.setLoading(true);
		this.isLoading = true;
		this.service
			.gerarGraficoIAPM(id, agrupamento, periodo, dataInicial, dataFinal)
			.subscribe({
				next: values => {
					this.relatorioService.atualizaVisibilidadeBotoesExportacao(values);
					this.dados =
						periodo === 'MENSAL'
							? values.map(value => ({
									...value,
									periodo: value.periodo.padStart(7, '0'),
								}))
							: values;
					this.fileName = gerarFilenameGrafico(
						`grafico_iapm_${this.inputs.form
							.get(FormularioRelatorio.AGRUPAMENTO)
							?.value.toLocaleLowerCase()}_${periodo.toLocaleLowerCase()}`
					);
					this.chartTitle = `IAPM - ${capitalizeFirstLetter(
						values[0].nome
					)} - ${this.periodoTitulo}`;
				},
				error: err => {
					this.toastr.error('Erro ao buscar as informações');
					this.isLoading = false;
					this.inputs.setLoading(false);
				},
				complete: () => {
					this.isLoading = false;
					this.inputs.setLoading(false);
				},
			});
	}

	private getDataFromForm(data: FormularioRelatorio) {
		return this.inputs.form?.get(data);
	}

	private handleFormatPeriodoBusca(periodo: string) {
		if (periodo === 'anual') return 'ANUAL';
		if (periodo === 'mensal') return 'MENSAL';
		if (periodo === 'anual_parcial') return 'ANUAL_PARCIAL';
		return 'PERIODO';
	}

	private handleRemoveValidatorsFromFormControl(data: FormularioRelatorio) {
		this.inputs.form.get(data)?.clearValidators();
		this.inputs.form.get(data)?.updateValueAndValidity({ emitEvent: false });
	}

	async exportarPNG() {
		if (!this.chart) {
			this.toastr.error(
				'Não é possível exportar PNG pois nenhum gráfico foi gerado.'
			);
			return;
		}

		try {
			const chartBase64 = await criarImagemBase64FromChart(this.chart);
			const baseImg = await this.carregarImagem(chartBase64);

			const canvas = document.createElement('canvas');
			canvas.width = baseImg.width;
			canvas.height = baseImg.height;
			const ctx = canvas.getContext('2d')!;

			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(0, 0, canvas.width, canvas.height);

			ctx.drawImage(baseImg, 0, 0, canvas.width, canvas.height);

			try {
				const margem = canvas.width * 0.02;

				const larguraLogoAesa = canvas.width * 0.08;
				const larguraLogoGoverno = canvas.width * 0.1; // aumentei só essa

				const logoAesa = await this.carregarImagem(
					'assets/images/logos/logoAesa.svg'
				);

				const alturaLogoAesa =
					(larguraLogoAesa / logoAesa.width) * logoAesa.height;

				const xLogoAesa = margem;

				const yLogoAesa = canvas.height - alturaLogoAesa - margem;

				ctx.drawImage(
					logoAesa,
					xLogoAesa,
					yLogoAesa,
					larguraLogoAesa,
					alturaLogoAesa
				);

				const logoGoverno = await this.carregarImagem(
					'assets/images/logos/governo_pb.png'
				);

				const alturaLogoGoverno =
					(larguraLogoGoverno / logoGoverno.width) * logoGoverno.height;

				const xLogoGoverno = canvas.width - larguraLogoGoverno - margem;

				const yLogoGoverno = canvas.height - alturaLogoGoverno - margem;

				ctx.drawImage(
					logoGoverno,
					xLogoGoverno,
					yLogoGoverno,
					larguraLogoGoverno,
					alturaLogoGoverno
				);
			} catch (e) {
				console.error('[IAPM PNG] Erro ao desenhar as logos:', e);
			}

			const dataUrl = canvas.toDataURL('image/png', 1);
			const nomeArquivo =
				this.fileName ||
				`grafico_iapm_${this.periodoTitulo || 'iapm'}`.replace(/\s+/g, '_');

			const a = document.createElement('a');
			a.href = dataUrl;
			a.download = `${nomeArquivo}.png`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		} catch (e) {
			console.error('[IAPM PNG] Falha ao exportar PNG:', e);
			this.toastr.error('Erro ao exportar o PNG do gráfico.');
		}
	}
}
